'use client';

import { StaticImport } from 'next/dist/shared/lib/get-img-props';
import Image, { ImageProps } from 'next/image';
import React, { useState, forwardRef, useMemo, useEffect } from 'react';

// แยก Props ที่ custom ออกมา
type CWImageProps = Omit<ImageProps, 'src' | 'alt'> & {
  src?: string | StaticImport | null;
  alt?: string;
  fallbackSrc?: string | StaticImport;
  placeholderComp?: React.ReactNode;
};

const CWImage = forwardRef<HTMLImageElement, CWImageProps>(
  (
    {
      src,
      fallbackSrc = '/placeholder.png', // Default fallback ถ้าไม่ส่งมา
      alt = 'image',
      placeholderComp,
      className,
      style,
      ...rest
    },
    ref,
  ) => {
    const [errorStage, setErrorStage] = useState<0 | 1 | 2>(0);
    useEffect(() => {
      setErrorStage(0);
    }, [src]);

    const isValidSrc = (source: unknown) => {
      if (!source) return false;
      if (typeof source === 'string' && source.trim() === '') return false;
      return true;
    };

    // Helper: จัดการ Path (Optional: จะตัดออกก็ได้ถ้ามั่นใจว่าส่ง path ถูก)
    const normalizeSrc = (source: string | StaticImport): string | StaticImport => {
      if (typeof source === 'string') {
        if (
          source.startsWith('http') ||
          source.startsWith('/') ||
          source.startsWith('data:')
        ) {
          return source;
        }
        return `/${source}`;
      }
      return source;
    };

    // Logic การเลือกรูปที่จะแสดง
    const finalSrc = useMemo(() => {
      if (errorStage === 0 && isValidSrc(src)) return normalizeSrc(src!);
      if (errorStage <= 1 && isValidSrc(fallbackSrc)) return normalizeSrc(fallbackSrc!);
      return null; // ไม่มีรูปจะแสดง
    }, [src, fallbackSrc, errorStage]);

    // กรณีไม่มีรูปเลย หรือ Load พังหมดแล้ว -> แสดง Placeholder Component
    if (!finalSrc || errorStage === 2) {
      if (placeholderComp) return <>{placeholderComp}</>;
      return (
        <div
          className={`bg-neutral-800 ${className || ''}`}
          style={{ width: '100%', height: '100%', ...style }}
          aria-label={alt}
          role="img"
        />
      );
    }

    // Logic เรื่อง Fill vs Width/Height
    // ถ้ามี width หรือ height ส่งมา -> fill=false/undefined
    // ถ้าไม่มี -> fill=true
    const isSized = !!rest.width || !!rest.height;
    const isFill = isSized ? false : (rest.fill ?? true);

    return (
      <Image
        ref={ref}
        alt={alt}
        src={finalSrc}
        className={className}
        style={style}
        // ถ้าไม่กำหนดขนาดมา ให้ใช้ fill=true เป็นค่าตั้งต้น
        fill={isFill}
        // ลบ props ที่อาจจะชนกันออก
        {...(isFill ? {} : { width: rest.width, height: rest.height })}
        {...rest} // Props อื่นๆ เช่น priority, sizes, quality
        onError={() => {
          // ถ้า Error ที่รูปหลัก -> ไป Fallback (Stage 1)
          // ถ้า Error ที่ Fallback -> ไป Placeholder (Stage 2)
          setErrorStage((prev) => (prev < 2 ? ((prev + 1) as 0 | 1 | 2) : prev));
        }}
      />
    );
  },
);

CWImage.displayName = 'CWImage';

export default CWImage;
